import { z } from 'zod';
import { inquiryAddressSchema } from '../inquiry/address.js';
import { inquiryStatusSchema } from '../inquiry/status.js';
import { uuidV7Schema } from '../uuid.js';
/**
 * Les **délais réglementaires de contrôle** : leur réglage par GHP, le jour où l'échéance d'une
 * demande tombe, et le contrat de l'écran « Relances » qui les lit.
 *
 * ⚠️ Les délais sont **des données**, réglées par GHP dans le back-office (« ghp-francis », option
 * A) : la base les porte (`deadline_setting`), le code ne les nomme plus.
 */
/**
 * La façon de compter les jours d'un délai : en **jours ouvrés** ou en **jours effectifs**. L'unité
 * fait partie de la règle, elle ne se devine pas du nombre.
 */
export const DEADLINE_UNIT_LABELS = {
    business: 'jours ouvrés',
    calendar: 'jours effectifs',
};
export const DEADLINE_UNITS = ['business', 'calendar'];
export const deadlineUnitSchema = z.enum(DEADLINE_UNITS);
/**
 * Les deux étapes d'un délai, comptées toutes deux **depuis la création de la demande** (consigne
 * du 15/10 : « le début de la demande, c'est quand elle est ajoutée dans l'app ») :
 *
 * - `appointment` — **Prise de RDV** : le premier rendez-vous est posé au plus tard ce jour-là. Le
 *   prototype l'écrit pour la vente : « Prise de RDV (contact) 3 j · RDV + validation 10 j » ;
 * - `visit` — **RDV + validation** : le contrôle est validé au plus tard ce jour-là.
 */
export const DEADLINE_STAGES = ['appointment', 'visit'];
export const deadlineStageSchema = z.enum(DEADLINE_STAGES);
export const DEADLINE_STAGE_LABELS = {
    appointment: 'Prise de RDV',
    visit: 'RDV + validation',
};
/**
 * Le plus long délai réglable : un an. ⚠️ **Une borne de sécurité, pas un choix métier** — le
 * calcul des bornes recule jour par jour (`lastCreationDayDueBy`), et un délai d'un milliard de
 * jours bloquerait le serveur. La base porte la même borne.
 */
export const MAX_DEADLINE_DAYS = 365;
/** Un délai « RDV + validation » : combien de jours, et comment ils se comptent. */
export const deadlineSchema = z.object({
    days: z.number().int().positive().max(MAX_DEADLINE_DAYS),
    unit: deadlineUnitSchema,
});
/**
 * Un **délai de relance réglé par GHP** (« ghp-francis », option A) : celui d'un type de contrôle,
 * pour tous les secteurs (`lotId: null`, le défaut) ou pour un secteur (son écart, #661).
 *
 * ⚠️ **Par type d'abord** : c'est la règle du 24/09 — un « Systématique » compte ses dix jours
 * ouvrés aux Mauges comme à Nantes. Le défaut livré est la règle donnée par Francis : vente,
 * systématique et SYAM à 10 jours ouvrés ; pollution, rétro et avant travaux à 70 jours effectifs.
 * Un secteur neuf a donc des délais dès son import, et une demande sans secteur aussi.
 */
export const deadlineSettingSchema = deadlineSchema.extend({
    /** Le type de contrôle — champ libre comme `Inquiry.purpose` (M2, M13). */
    purpose: z.string().trim().min(1),
    /** Le secteur (`Lot`) dont c'est l'écart, ou `null` : le défaut, pour tous les secteurs. */
    lotId: uuidV7Schema.nullable(),
    /** L'étape : prise de RDV ou RDV + validation. */
    stage: deadlineStageSchema,
});
/**
 * Ce que l'écran envoie pour **remplacer** les délais d'une portée — le défaut (`lotId: null`)
 * ou un secteur. Un type absent de la liste n'a plus de délai dans cette portée : pour un secteur, il
 * retombe sur le défaut ; pour le défaut, il n'a plus d'échéance.
 */
export const replaceDeadlineSettingsSchema = z.strictObject({
    lotId: uuidV7Schema.nullable(),
    deadlines: z
        .array(deadlineSchema.extend({
        purpose: z.string().trim().min(1).max(100),
        // Sans étape : le RDV + validation, seul délai que l'écran envoyait avant la prise de RDV.
        stage: deadlineStageSchema.default('visit'),
    }))
        .max(50)
        .refine((rows) => new Set(rows.map((row) => `${row.stage}:${row.purpose}`)).size === rows.length, 'un type de contrôle ne porte qu’un délai par étape et par portée'),
});
/**
 * Le délai d'une demande à une étape (le RDV + validation par défaut) : l'écart de son secteur pour
 * son type, sinon le défaut de son type.
 *
 * `lotId` est le secteur du lieu (`Site.lot_id`), `null` sans secteur. `null` — donc aucune
 * échéance, ni « Hors délai » ni « sous 15 j » — quand le type n'a pas de délai réglé : l'écran le
 * dit plutôt que d'inventer une échéance.
 */
export function deadlineOf(purpose, lotId, settings, stage = 'visit') {
    if (purpose === null)
        return null;
    const ruleFor = (scope) => settings.find((setting) => setting.purpose === purpose && setting.lotId === scope && setting.stage === stage);
    const rule = (lotId === null ? undefined : ruleFor(lotId)) ?? ruleFor(null);
    return rule === undefined ? null : { days: rule.days, unit: rule.unit };
}
/**
 * Le fuseau dans lequel les jours se comptent.
 *
 * ⚠️ **Constante métier, pas variable d'environnement** — même constante que la confirmation de
 * rendez-vous et le fichier d'export, pour la même raison : un conteneur n'a pas de fuseau local,
 * et compter les jours en UTC daterait de la veille une demande arrivée à 23 h 30.
 */
export const DEADLINE_TIME_ZONE = 'Europe/Paris';
/**
 * La fenêtre « Échéance sous 15 j » du prototype, en jours **civils**.
 *
 * Ce n'est pas un délai réglementaire mais une fenêtre de vigilance : elle dit « à traiter cette
 * quinzaine », donc elle se compte comme un calendrier se lit.
 */
export const DUE_SOON_DAYS = 15;
const DAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
    timeZone: DEADLINE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});
/**
 * Le **jour civil français** d'un instant, en `AAAA-MM-JJ`.
 *
 * Composé depuis `formatToParts` plutôt que pris tel quel : le motif d'une locale est une donnée
 * d'ICU, et une montée de version qui l'ordonnerait autrement casserait toute l'arithmétique
 * ci-dessous sans qu'aucun type ne bouge.
 */
export function dayOf(instant) {
    const parts = DAY_FORMAT.formatToParts(instant);
    const value = (type) => parts.find((part) => part.type === type)?.value ?? '';
    return `${value('year')}-${value('month')}-${value('day')}`;
}
/**
 * Le jour d'échéance d'une demande créée à cet instant : le dernier jour où le contrôle peut
 * encore être validé dans les temps.
 *
 * ⚠️ **Une échéance est un jour, pas un instant**, et c'est ce qui rend le calcul honnête : « 10
 * jours ouvrés » ne dit rien d'une heure, et deux demandes du même jour ont la même échéance —
 * celle du matin ne peut pas être en retard pendant que celle de l'après-midi tient encore.
 *
 * `null` sans délai (`deadlineFor`) : sans règle, pas d'échéance à afficher.
 */
export function dueDayOf(createdAt, deadline) {
    if (deadline === null)
        return null;
    return dueDayFromDay(dayOf(createdAt), deadline);
}
/**
 * Les deux bornes que le serveur compare à la **date de création** d'une demande, pour un délai.
 *
 * ⚠️ C'est ce qui permet de compter et de filtrer **en base** (D24) sans y réécrire le calcul des
 * jours ouvrés : la règle est appliquée une fois, ici, et le `WHERE` n'est plus qu'une comparaison
 * de dates. Le SQL et l'écran ne peuvent donc pas diverger — ils lisent le même jour.
 *
 * ⚠️ L'instant `now` est un **paramètre**, jamais `new Date()` : c'est l'appelant qui date, et un
 * test qui ne peut pas figer l'horloge d'une fonction ne peut pas l'exercer (#107, #165).
 */
export function deadlineCutoffs(now, deadline) {
    const today = dayOf(now);
    return {
        // Échéance « dépassée » = le jour d'échéance est **révolu**. Une demande due aujourd'hui a
        // encore sa journée : la compter en retard afficherait un retard que personne ne pourrait
        // corriger avant le lendemain.
        overdueOnOrBefore: lastCreationDayDueBy(addDays(today, -1), deadline),
        dueSoonOnOrBefore: lastCreationDayDueBy(addDays(today, DUE_SOON_DAYS), deadline),
    };
}
/**
 * Les motifs de relance — les seuls **dérivables** du modèle d'aujourd'hui :
 *
 * - `missed_appointment` — le rendez-vous est passé, rien n'est transmis ;
 * - `past_deadline` — le délai « RDV + validation » est dépassé ;
 * - `late_appointment` — **RDV à prendre** : le délai de prise de RDV est dépassé, et aucun
 *   rendez-vous n'a jamais été posé. Un rendez-vous posé, même annulé depuis, a tenu l'étape.
 *
 * ⚠️ Les motifs « Relance client 1ʳᵉ » et « 2ᵉ » du prototype n'y sont pas : rien ne les
 * déclenche, aucune colonne ne garde la trace d'un envoi, et le prototype lui-même se contentait
 * d'un message simulé au clic. Les servir ferait croire à un suivi qui n'existe pas.
 */
export const REMINDER_REASONS = ['missed_appointment', 'past_deadline', 'late_appointment'];
export const reminderReasonSchema = z.enum(REMINDER_REASONS);
/**
 * Les statuts où un rendez-vous passé **fait défaut** : le contrôle est encore attendu, et
 * personne n'a rien transmis.
 *
 * `submitted` et `validated` en sont exclus — le contrôle a eu lieu, quelle qu'ait été la date du
 * rendez-vous. `archived` et `dropped` aussi : l'un est au-delà de la validation, l'autre est un
 * abandon assumé ; les relancer serait proposer un travail que personne n'attend plus.
 */
export const REMINDER_MISSED_APPOINTMENT_STATUSES = [
    'received',
    'assigned',
    'in_progress',
];
/**
 * Les statuts où un rendez-vous **reste à prendre** : la demande n'est pas encore sur le terrain.
 * Un contrôle en cours ou transmis a eu lieu, rendez-vous ou pas (un systématique sans RDV).
 */
export const REMINDER_LATE_APPOINTMENT_STATUSES = ['received', 'assigned'];
/**
 * Les statuts où le délai réglementaire **court encore**.
 *
 * ⚠️ `submitted` en fait partie, contrairement au motif du rendez-vous, et ce n'est pas une
 * distraction : la règle dit « RDV + validation 10 j ». Un contrôle transmis que le bureau n'a
 * pas validé n'a donc pas tenu le délai — c'est même la seule file sur laquelle le bureau peut
 * agir seul.
 */
export const REMINDER_DEADLINE_STATUSES = [
    'received',
    'assigned',
    'in_progress',
    'submitted',
];
/**
 * Une ligne de l'écran « Relances » : la demande, ce qu'on sait de son rendez-vous, son échéance,
 * et **pourquoi** elle est là.
 *
 * L'adresse est aplatie et nullable pour la raison habituelle : c'est la même chaîne de jointure
 * qu'ailleurs, et les colonnes de l'existant sont trouées (M21).
 */
export const reminderRowSchema = z.object({
    id: uuidV7Schema,
    /** La case « Urgent » du bureau (Sentry BACK-OFFICE-X), dite en rouge sur la ligne. */
    urgent: z.boolean().default(false),
    reference: z.string().nullable(),
    caseReference: z.string().nullable(),
    status: inquiryStatusSchema,
    /**
     * Le **type de contrôle** — la colonne « Type » du prototype.
     *
     * C'est **lui qui donne le délai** (`deadlineFor`), comme au prototype : un « Systématique »
     * compte dix jours ouvrés, où qu'il soit.
     *
     * Champ libre et nullable, comme la colonne (M2, M13) : `inquiryPurposeLabel` en donne le
     * libellé, avec son repli sur la valeur brute.
     */
    purpose: z.string().nullable(),
    address: inquiryAddressSchema,
    /**
     * Le délai appliqué (`deadlineFor`), `null` sans règle. Le serveur le rend avec l'échéance :
     * l'écran affiche la règle qui a daté la ligne, sans la recalculer.
     */
    deadline: deadlineSchema.nullable(),
    createdAt: z.iso.datetime({ offset: true }),
    /**
     * Le **dernier** rendez-vous posé, ou `null` si la demande n'en a jamais eu. Le dernier et non
     * le premier : un report en crée un second, et c'est lui que l'usager attend.
     */
    lastAppointmentAt: z.iso.datetime({ offset: true }).nullable(),
    /** Le jour d'échéance (`dueDayOf`), `null` sans délai (`deadlineFor`). */
    dueDay: z.iso.date().nullable(),
    /** Au moins un — une ligne sans motif n'aurait aucune raison d'être dans la liste. */
    reasons: z.array(reminderReasonSchema).min(1),
});
/** Les vues de l'écran : tout ce qui est en retard, ou l'un des deux motifs. */
export const REMINDER_REASON_FILTERS = ['all', ...REMINDER_REASONS];
export const reminderReasonFilterSchema = z.enum(REMINDER_REASON_FILTERS);
/**
 * Ce que le serveur accepte pour construire une page de relances — la **seule** définition dont
 * dérivent l'URL de l'écran, le `WHERE` du dépôt et l'appel du client typé (D24).
 *
 * Schéma **strict** : la tolérance M21 vit à l'entrée HTTP (`apps/api/src/inquiries/…`), comme
 * pour le tableau des demandes.
 */
export const reminderFilterSchema = z.object({
    reason: reminderReasonFilterSchema,
    limit: z.number().int().positive(),
    offset: z.number().int().min(0),
});
/**
 * Les quatre compteurs de l'écran, comptés sur **l'ensemble** des demandes — jamais sur la page
 * affichée (D24).
 *
 * ⚠️ `missedAppointment` et `pastDeadline` **se recouvrent** : une demande peut être en retard
 * pour les deux motifs, et leur somme dépasse alors `all`. C'est juste — ce sont deux questions,
 * pas deux parts d'un gâteau.
 *
 * ⚠️ `dueSoon` ne compte **aucune** ligne de la liste : une échéance à venir n'est pas un retard.
 * C'est le compteur qui prévient avant que la ligne n'apparaisse.
 *
 * ⚠️ Le compteur « Relances à envoyer » du prototype n'a pas d'équivalent, et son absence est le
 * sujet : aucune colonne ne dit qu'une relance est partie (cf. `REMINDER_REASONS`). Un compteur
 * en dur est exactement ce qu'on corrige ailleurs (#179).
 */
export const reminderCountsSchema = z.object({
    all: z.number().int().min(0),
    missedAppointment: z.number().int().min(0),
    pastDeadline: z.number().int().min(0),
    /** « RDV à prendre » : la prise de RDV a dépassé son délai. */
    lateAppointment: z.number().int().min(0),
    dueSoon: z.number().int().min(0),
});
/** L'enveloppe paginée de l'écran (D24) — même forme que les autres listes du bureau. */
export const reminderPageSchema = z.object({
    items: z.array(reminderRowSchema),
    /** L'effectif de la vue **active**, sur l'ensemble. Jamais `items.length`. */
    total: z.number().int().min(0),
    counts: reminderCountsSchema,
});
/**
 * Le jour d'échéance d'une demande créée ce jour-là. Les jours ouvrés **ignorent les jours
 * fériés** : importer un calendrier officiel pour un écran de suivi ne se justifie pas
 * aujourd'hui, et l'écran le dit plutôt que de le taire.
 *
 * ⚠️ **De combien, et dans quel sens.** Dire « on ignore les fériés » sans l'amplitude laisse
 * l'opérateur sans moyen de savoir ce qu'il regarde. Mesuré sur les 365 jours de 2026 contre les
 * 11 fériés légaux, délai de Nantes (10 jours ouvrés) : l'écart monte à **5 jours** (création du
 * 29/04 → échéance annoncée le 13/05, réelle le 18/05), et il est **unilatéral** — aucun jour de
 * l'année où l'échéance annoncée serait plus tardive que la réelle. Le biais est donc toujours
 * « trop tôt », jamais « trop tard » : prudent pour une relance, mais il fait afficher le badge
 * « Hors délai » avant l'heure autour des ponts. L'`InfoNote` de l'écran dit désormais cette
 * conséquence, et non plus la seule cause (revue de #203).
 */
function dueDayFromDay(creationDay, { days, unit }) {
    return unit === 'business' ? addBusinessDays(creationDay, days) : addDays(creationDay, days);
}
/**
 * Le **dernier jour de création** dont l'échéance tombe au plus tard le jour donné.
 *
 * Une recherche à reculons plutôt qu'une soustraction de jours ouvrés : la même fonction
 * (`dueDayFromDay`) sert à l'aller et au retour, donc les deux sens ne peuvent pas diverger — une
 * soustraction symétrique, elle, se trompe d'un jour dès qu'une création tombe un week-end.
 *
 * Le point de départ est le jour limite lui-même : aucune échéance ne précède sa création, il
 * borne donc la recherche par le haut. La boucle recule d'au plus le délai.
 */
function lastCreationDayDueBy(limitDay, deadline) {
    let candidate = limitDay;
    while (dueDayFromDay(candidate, deadline) > limitDay) {
        candidate = addDays(candidate, -1);
    }
    return candidate;
}
/**
 * Le jour, ramené à un instant **à midi UTC**. Midi et non minuit : aucun décalage de fuseau, ni
 * changement d'heure, ne peut faire basculer un midi sur le jour voisin.
 */
function midday(day) {
    return new Date(`${day}T12:00:00.000Z`);
}
function addDays(day, count) {
    const moved = midday(day);
    moved.setUTCDate(moved.getUTCDate() + count);
    return moved.toISOString().slice(0, 'AAAA-MM-JJ'.length);
}
function addBusinessDays(day, count) {
    let current = day;
    for (let remaining = count; remaining > 0; remaining -= 1) {
        do {
            current = addDays(current, 1);
        } while (isWeekend(current));
    }
    return current;
}
/** Samedi et dimanche — les seuls jours non ouvrés que ce calcul connaisse. */
function isWeekend(day) {
    const weekday = midday(day).getUTCDay();
    return weekday === 0 || weekday === 6;
}
//# sourceMappingURL=deadline.js.map